import { Controller, get, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { InputActionMeta } from "react-select";

import { AddressV2 } from "api/models/company.model";
import { ReactComponent as EditIcon } from "assets/images/iconsV2/edit.svg";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus-02.svg";
import AddressV2Dialog from "components/organisms/AddressV2Dialog/AddressV2Dialog";
import Button from "components/v2/Button/Button";
import Radio from "components/v2/Radio/Radio";
import Select from "components/v2/Select/Select";
import {
  useSharedAddress,
  UseSharedAddressConfig,
} from "@RequestFormV2/sections/Schedule/hooks/useSharedAddress";
import { ReviewFieldErrorClearWatcher } from "@RequestFormV2/hooks/ReviewFieldErrorClearWatcher";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";
import { useRequestFormReviewValidation } from "@RequestFormV2/context/reviewValidation";
import {
  formatAddressOptionLabel,
} from "@RequestFormV2/sections/Schedule/utils/selectOptionLabel";

export interface AddressDialogConfig {
  canAddAddressBookEntry: boolean;
  showAddressDialog: boolean;
  dialogSelectedAddress?: AddressV2;
  location?: string;
  openAddAddressDialog: () => void;
  openEditAddressDialog: () => void;
  closeAddressDialog: () => void;
}

interface SharedAddressSectionProps extends UseSharedAddressConfig {
  testIdPrefix?: string;
  addressSearchTestId?: string;
  portTestId?: string;
  addressSearchPlaceholder?: string;
  addressSelectErrorMessage?: string;
  addressDialog?: AddressDialogConfig;
}

export default function SharedAddressSection({
  testIdPrefix,
  addressSearchTestId,
  portTestId,
  addressSearchPlaceholder,
  addressSelectErrorMessage,
  addressDialog,
  addressTypeResolver,
  ...config
}: SharedAddressSectionProps) {
  const intl = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { hasAttemptedReview, clearFieldError } = useRequestFormReviewValidation();
  const address = useSharedAddress({ addressTypeResolver, ...config });

  const {
    fieldPaths,
    formatForRadio,
    addressTypeRadioOptions,
    handleAddressTypeChange,
    fieldsDisabled,
    showPortField,
    isAirport,
    portCodeFieldName,
    portOptions,
    isLoadingPorts,
    portPlaceholder,
    formatPortOptionLabelForSelect,
    handlePortInputChange,
    handlePortSelect,
    addressFieldsRequired,
    addressSelectOptions,
    isFetchingAddresses,
    isOptionalAirportOrSeaport,
    handleAddressSelect,
    handleAddressInputChange,
    selectedAddressSummary,
    handleDialogAddressSuccess,
    refetchAddresses,
  } = address;

  const showTypeRadio = address.addressTypeOptions.length > 1;
  const addressRequired = addressFieldsRequired && !isOptionalAirportOrSeaport;

  useReactiveReviewClear(
    fieldPaths.address_id,
    () => isOptionalAirportOrSeaport,
    [isOptionalAirportOrSeaport],
  );

  const addressIdError = get(errors, fieldPaths.address_id) as { message?: string } | undefined;
  const requiredAddressErrorMessage =
    !isOptionalAirportOrSeaport && addressIdError?.message ? String(addressIdError.message) : undefined;
  const resolvedAddressSelectErrorMessage = addressSelectErrorMessage ?? requiredAddressErrorMessage;
  const addressSelectHasError = Boolean(resolvedAddressSelectErrorMessage);

  const options = addressTypeRadioOptions.map((option) => ({
    ...option,
    disabled: fieldsDisabled,
  }));

  const portLabel = isAirport
    ? intl.formatMessage({ id: "general.airport", defaultMessage: "Airport" })
    : intl.formatMessage({ id: "request-form.schedule.address.port", defaultMessage: "Port" });

  const addressSearchLabel = showPortField
    ? intl.formatMessage({ id: "request-form.schedule.address.yourAddress", defaultMessage: "Your address" })
    : undefined;

  const isPortAddressType = (value: unknown) =>
    addressTypeResolver
      ? addressTypeResolver.isAirport(value) || addressTypeResolver.isSeaport(value)
      : value === "airport" || value === "seaport";

  const handleAddressTypeRadioChange = (radioValue: string, fieldOnChange: (value: unknown) => void) => {
    handleAddressTypeChange(radioValue, fieldOnChange);

    if (!hasAttemptedReview || !clearFieldError) return;

    const parsedValue = addressTypeResolver?.parseFromRadio(radioValue) ?? radioValue;
    if (isPortAddressType(parsedValue)) {
      clearFieldError(fieldPaths.address_id);
    }
  };

  return (
    <div className="rfv2-form-section__content rfv2-schedule-address">
      {showTypeRadio && (
        <Controller
          name={fieldPaths.address_type}
          control={control}
          render={({ field }) => (
            <Radio
              isInline
              size="md"
              options={options}
              value={formatForRadio(field.value)}
              onChange={(val) => handleAddressTypeRadioChange(val, field.onChange)}
              getOptionTestId={testIdPrefix ? (option) => `${testIdPrefix}-${option.value}` : undefined}
            />
          )}
        />
      )}

      <div className="rfv2-schedule-address-fields">
        {showPortField && (
          <Select
            name={portCodeFieldName}
            required={addressFieldsRequired}
            label={portLabel}
            placeholder={portPlaceholder}
            options={portOptions}
            onChange={handlePortSelect}
            onInputChange={handlePortInputChange}
            filterOption={() => true}
            isLoading={isLoadingPorts}
            formatOptionLabel={formatPortOptionLabelForSelect}
            className="rfv2-schedule-port-select"
            isClearable
            isDisabled={fieldsDisabled}
            testId={portTestId}
          />
        )}

        <ReviewFieldErrorClearWatcher fieldPath={fieldPaths.address_id} />

        <Select
          name={fieldPaths.address_reference_number}
          required={addressRequired}
          hasError={addressSelectHasError}
          errorMessage={resolvedAddressSelectErrorMessage}
          label={addressSearchLabel}
          placeholder={
            addressSearchPlaceholder ??
            intl.formatMessage({
              id: "new-request.schedule.chooseAddressV2Placeholder",
              defaultMessage: "Search by address, location reference number, or custom reference",
            })
          }
          options={addressSelectOptions ?? []}
          onChange={handleAddressSelect}
          onInputChange={(val: string, meta: InputActionMeta) => handleAddressInputChange(val, meta)}
          filterOption={() => true}
          isLoading={isFetchingAddresses}
          formatOptionLabel={formatAddressOptionLabel}
          isClearable
          isDisabled={fieldsDisabled}
          testId={addressSearchTestId}
        />

        {selectedAddressSummary.hasContent && (
          <div className={addressDialog?.canAddAddressBookEntry ? "rfv2-schedule-selected-address-row" : undefined}>
            <div className="rfv2-schedule-selected-address">
              {selectedAddressSummary.street && <>{selectedAddressSummary.street}</>}
              {(selectedAddressSummary.cityName || selectedAddressSummary.stateName || selectedAddressSummary.zipcode) && (
                <>
                  {selectedAddressSummary.cityName && `, ${selectedAddressSummary.cityName}`}
                  {selectedAddressSummary.stateName && `, ${selectedAddressSummary.stateName}`}
                  {selectedAddressSummary.zipcode && ` ${selectedAddressSummary.zipcode}`}
                </>
              )}
              {selectedAddressSummary.countryName && <>{`, ${selectedAddressSummary.countryName}`}</>}
            </div>
            {addressDialog?.canAddAddressBookEntry && (
              <button
                type="button"
                className="rfv2-schedule-selected-address-edit"
                onClick={addressDialog.openEditAddressDialog}
                aria-label={intl.formatMessage({ id: "general.edit", defaultMessage: "Edit" })}
              >
                <EditIcon />
              </button>
            )}
          </div>
        )}

        {addressDialog?.canAddAddressBookEntry && (
          <>
            <Button
              type="button"
              variant="link-gray"
              size="sm"
              className="rfv2-schedule-link-action"
              iconLeft={<PlusIcon />}
              label={intl.formatMessage({
                id: "request-form.schedule.newAddress",
                defaultMessage: "New address",
              })}
              onClick={addressDialog.openAddAddressDialog}
            />

            {addressDialog.showAddressDialog && (
              <AddressV2Dialog
                isOpen={addressDialog.showAddressDialog}
                onClose={addressDialog.closeAddressDialog}
                refetchAddresses={refetchAddresses}
                source="quick_quote_request"
                onAddNewAddress={handleDialogAddressSuccess}
                formSelectedShipperLocation={addressDialog.location}
                selectedAddress={addressDialog.dialogSelectedAddress}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
